import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import {
  DataFeedUpload,
  DataFeedUploadHistoryEntry,
  ImportUploadItemPreview,
  ImportUploadPreview,
  ImportUploadRelationship,
  ImportUploadWarning,
  ImportUploadWarningLevel,
} from '../../../models/data-feed.model';
import { EtlMonitorService } from '../../../services/etl-monitor.service';
import { BadgeMeta, describeHttpError, formatCompact, person, prettyJson, saveBlob, stageMeta } from '../data-feed-ui';

export interface DataFeedImportConfirmDialogData {
  preview: ImportUploadPreview;
  /** History view: the package details as they were shown, with no Queue / Discard actions. */
  readOnly?: boolean;
  entry?: DataFeedUploadHistoryEntry | null;
  manifestJson?: string | null;
  run?: DataFeedUpload | null;
}

/** confirm: queue the import. discard: delete the staged zip. Dismissed: leave it pending. */
export type DataFeedImportConfirmChoice = 'confirm' | 'discard';

interface ModeMeta extends BadgeMeta {
  /** Longer explanation for the tooltip. */
  detail: string;
}

const EXISTING_META: Record<string, ModeMeta> = {
  Overwrite: {
    label: 'Overwrite', icon: 'published_with_changes', tone: 'warning',
    detail: 'Every field of a record that already exists is replaced by the file’s record.',
  },
  Merge: {
    label: 'Merge', icon: 'merge', tone: 'success',
    detail: 'Only the fields present in the file are updated; the other fields of existing records are kept.',
  },
  MergeIfEnabled: {
    label: 'Merge*', icon: 'merge', tone: 'neutral',
    detail: 'The entity is retained. With partial updates enabled for this organization existing records are merged; '
      + 'with them disabled offers fail as "already exists" and other entities are overwritten.',
  },
};

const MISSING_META: Record<string, ModeMeta> = {
  Kept: {
    label: 'Kept', icon: 'shield', tone: 'success',
    detail: 'Existing records that are not in the file stay as they are.',
  },
  SoftDeleted: {
    label: 'Soft-deleted', icon: 'delete_sweep', tone: 'failed',
    detail: 'After the load, every existing record of this entity whose ClientKey is not in the file is soft-deleted. '
      + 'Skipped automatically when any record of the file failed or too few records loaded.',
  },
  NotApplicable: {
    label: 'n/a', icon: 'remove', tone: 'neutral',
    detail: 'Not a master entity: records missing from the file are never touched.',
  },
};

const RELATIONSHIP_META: Record<string, ModeMeta> = {
  Replaced: {
    label: 'Replaced', icon: 'sync_alt', tone: 'warning',
    detail: 'Relationships are synced to the file: links not in the file are removed, and an empty list removes them all.',
  },
  Kept: {
    label: 'Kept + added', icon: 'add_link', tone: 'success',
    detail: 'Existing relationships are kept and the file’s are added.',
  },
};

const LEVEL_META: Record<ImportUploadWarningLevel, BadgeMeta> = {
  Info: { label: 'Info', icon: 'info', tone: 'processing' },
  Warning: { label: 'Warning', icon: 'warning', tone: 'warning' },
  Error: { label: 'Error', icon: 'error', tone: 'failed' },
};

/**
 * Last confirmation before an uploaded import package is queued: per file, what retaineditems makes the import do to
 * existing records, to records missing from the file and to relationships, plus the problems the worker would hit
 * (malformed JSON, missing files, unknown entities, wrong organization). Closes with 'confirm', 'discard' or nothing.
 */
@Component({
  selector: 'app-data-feed-import-confirm-dialog',
  templateUrl: './data-feed-import-confirm-dialog.component.html',
  styleUrls: ['./data-feed-import-confirm-dialog.component.scss'],
})
export class DataFeedImportConfirmDialogComponent {
  readonly preview: ImportUploadPreview;
  readonly readOnly: boolean;
  readonly entry: DataFeedUploadHistoryEntry | null;
  readonly manifestJson: string;
  readonly run: DataFeedUpload | null;
  showManifest = false;
  downloading = false;
  readonly displayedColumns = ['entity', 'records', 'existing', 'missing', 'relationships'];
  /** Package warnings first, then the items' warnings, errors first. */
  readonly warnings: ImportUploadWarning[];
  readonly errorCount: number;
  readonly warningCount: number;
  acknowledged = false;

  constructor(
    private readonly dialogRef: MatDialogRef<DataFeedImportConfirmDialogComponent, DataFeedImportConfirmChoice>,
    private readonly etlMonitorService: EtlMonitorService,
    private readonly snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) data: DataFeedImportConfirmDialogData
  ) {
    this.preview = data.preview;
    this.readOnly = !!data.readOnly;
    this.entry = data.entry ?? null;
    this.manifestJson = prettyJson(data.manifestJson);
    this.run = data.run ?? null;
    const rank: Record<ImportUploadWarningLevel, number> = { Error: 0, Warning: 1, Info: 2 };
    this.warnings = [
      ...(this.preview.Warnings ?? []),
      ...(this.preview.Items ?? []).flatMap(i => i.Warnings ?? []),
    ].sort((a, b) => rank[a.Level] - rank[b.Level]);
    this.errorCount = this.warnings.filter(w => w.Level === 'Error').length;
    this.warningCount = this.warnings.filter(w => w.Level === 'Warning').length;
  }

  /** Who uploaded the package: "Name (e-mail)" when the name is known. */
  get uploader(): string {
    return person(this.preview.UploadedByName, this.preview.UploadedBy, true);
  }

  get title(): string {
    if (this.readOnly) { return 'Package details'; }
    return this.preview.CanConfirm ? 'Confirm this import?' : 'This package cannot be imported';
  }

  /** History view: what was decided about this upload. */
  get decisionText(): string | null {
    const e = this.entry;
    if (!e) { return null; }
    const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');
    switch ((e.Status || '').toLowerCase()) {
      case 'confirmed': return `Confirmed by ${person(e.ConfirmedByName, e.ConfirmedBy, true) || 'unknown'} on ${when(e.ConfirmedAt)}`;
      case 'discarded': return `Discarded by ${person(e.DiscardedByName, e.DiscardedBy, true) || 'unknown'} on ${when(e.DiscardedAt)}`;
      case 'rejected': return `Rejected at upload on ${when(e.UploadedAt)}${e.RejectReason ? ': ' + e.RejectReason : ''}`;
      default: return 'Still waiting for a decision';
    }
  }

  runMeta(): BadgeMeta | null {
    return this.run ? stageMeta(this.run.Stage) : null;
  }

  get canDownload(): boolean {
    return !!this.entry?.CanDownload;
  }

  /** History view: the archived zip. */
  downloadPackage(): void {
    const entry = this.entry;
    if (!entry?.CanDownload || this.downloading) { return; }
    this.downloading = true;
    this.etlMonitorService.downloadUploadPackage(entry.UploadId).pipe(finalize(() => this.downloading = false)).subscribe({
      next: response => {
        if (response.body) {
          saveBlob(response.body, entry.FileName || this.preview.FileName || 'package.zip');
        }
      },
      error: err => this.snackBar.open(describeHttpError(err, 'Could not download the package'), 'Close', { duration: 6000 }),
    });
  }

  get canConfirm(): boolean {
    return this.preview.CanConfirm && (!this.preview.RequiresAcknowledgement || this.acknowledged);
  }

  get destructiveCount(): number {
    return (this.preview.Items ?? []).filter(i => i.IsDestructive).length;
  }

  get totalRecords(): number | null {
    const counted = (this.preview.Items ?? []).filter(i => i.RecordCount !== null && i.RecordCount !== undefined);
    return counted.length ? counted.reduce((sum, i) => sum + (i.RecordCount ?? 0), 0) : null;
  }

  /** What the acknowledge checkbox asks the user to accept. */
  get acknowledgeText(): string {
    const parts: string[] = [];
    if (this.destructiveCount > 0) {
      parts.push(`${this.destructiveCount} file${this.destructiveCount === 1 ? '' : 's'} that soft-delete${this.destructiveCount === 1 ? 's' : ''} records or replace${this.destructiveCount === 1 ? 's' : ''} relationships`);
    }
    if (this.warningCount > 0) {
      parts.push(`the ${this.warningCount} warning${this.warningCount === 1 ? '' : 's'} above`);
    }
    return `I have reviewed ${parts.join(' and ')} and want to load this file`;
  }

  existingMeta(item: ImportUploadItemPreview): ModeMeta {
    return EXISTING_META[item.ExistingRecords] ?? { label: item.ExistingRecords, icon: 'help_outline', tone: 'neutral', detail: '' };
  }

  missingMeta(item: ImportUploadItemPreview): ModeMeta {
    return MISSING_META[item.MissingRecords] ?? { label: item.MissingRecords, icon: 'help_outline', tone: 'neutral', detail: '' };
  }

  relationshipMeta(rel: ImportUploadRelationship): ModeMeta {
    return RELATIONSHIP_META[rel.Mode] ?? { label: rel.Mode, icon: 'help_outline', tone: 'neutral', detail: '' };
  }

  levelMeta(warning: ImportUploadWarning): BadgeMeta {
    return LEVEL_META[warning.Level] ?? LEVEL_META.Info;
  }

  hasError(item: ImportUploadItemPreview): boolean {
    return (item.Warnings ?? []).some(w => w.Level === 'Error');
  }

  records(item: ImportUploadItemPreview): string {
    if (!item.FileFound) { return 'missing'; }
    if (this.hasError(item)) { return 'invalid'; }
    if (item.RecordCount === null || item.RecordCount === undefined) { return '–'; }
    return formatCompact(item.RecordCount);
  }

  exactRecords(item: ImportUploadItemPreview): string {
    return item.RecordCount === null || item.RecordCount === undefined ? '' : `${item.RecordCount.toLocaleString('en-US')} records`;
  }

  size(bytes: number | null | undefined): string {
    if (bytes === null || bytes === undefined) { return ''; }
    if (bytes < 1024) { return `${bytes} B`; }
    if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB`; }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  trackByEntity(_: number, item: ImportUploadItemPreview): string {
    return `${item.Entity}:${item.FileName}`;
  }

  confirm(): void {
    if (this.canConfirm) {
      this.dialogRef.close('confirm');
    }
  }

  discard(): void {
    this.dialogRef.close('discard');
  }
}
